import React from "react";
import { Link } from "react-router-dom";
import appIcon from "../assets/tab-group-share/icon128.png";
import promoBanner from "../assets/tab-group-share/promo-marquee-1400x560.png";
import exportScreenshot from "../assets/tab-group-share/screenshot-export-1280x800.png";
import importScreenshot from "../assets/tab-group-share/screenshot-import-1280x800.png";
import TabGroupShareStoreButton from "../components/TabGroupShareStoreButton";
import styles from "../InverluxApps.module.css";

const exampleGroup = {
  title: "Web Research",
  color: "blue",
  collapsed: false,
  tabs: [
    { title: "MDN Web Docs", url: "https://developer.mozilla.org/" },
    { title: "Chrome Developers", url: "https://developer.chrome.com/" },
  ],
};

const exportExamples = [
  {
    name: "Plain Text",
    description:
      "A readable list of titles and URLs for email, notes, spreadsheets, or any tool that accepts text.",
    preview:
      "Tab Group: " + exampleGroup.title + "\n\n" +
      exampleGroup.tabs.map((tab) => tab.title + "\n" + tab.url).join("\n\n"),
  },
  {
    name: "Markdown",
    description:
      "Linked titles ready to paste into AI prompts, Markdown notes, and other text tools.",
    preview:
      "## " + exampleGroup.title + "\n\n" +
      exampleGroup.tabs
        .map((tab) => "- [" + tab.title + "](<" + tab.url + ">)")
        .join("\n"),
  },
  {
    name: "JSON",
    description:
      "Structured tab data with the group’s title, color, collapsed state, and tab order for sharing and importing.",
    preview: JSON.stringify(
      { format: "tab-group-share", version: 1, group: exampleGroup },
      null,
      2
    ),
  },
];

const TabGroupShare = () => (
  <>
    <section className={styles.productHero}>
      <div className={styles.contentWidth}>
        <div className={styles.productHeroGrid}>
          <div>
            <div className={styles.productIdentity}>
              <img
                className={styles.heroProductIcon}
                src={appIcon}
                alt="Tab Group Share icon"
                width="92"
                height="92"
              />
              <div>
                <div className={styles.statusRow}>Chrome extension · Coming soon</div>
                <h1>Tab Group Share</h1>
              </div>
            </div>
            <p className={styles.developedBy}>By Inverlux LLC.</p>
            <p className={styles.heroLead}>
              Your research tabs, ready for your next idea.
            </p>
            <p className={styles.heroSupporting}>
              Collect tabs on a topic, then copy the whole group’s titles and
              URLs into AI prompts, notes, spreadsheets, email, or other text
              tools. Choose Plain Text, Markdown, or JSON, and paste wherever
              you work.
            </p>
            <div className={styles.heroActions}>
              <TabGroupShareStoreButton />
              <Link className={styles.textLink} to="/tab-group-share/privacy">
                Read the Privacy Policy
              </Link>
            </div>
          </div>
          <div className={styles.heroAssurance}>
            <p className={styles.assuranceLabel}>Keep your research moving</p>
            <ul className={styles.checkList}>
              <li>Copy a whole group as Plain Text, Markdown, or JSON</li>
              <li>Paste, preview, and confirm before importing</li>
              <li>Preserve tab order and duplicates; existing tabs stay open</li>
              <li>Local processing, with no automatic clipboard reading</li>
            </ul>
            <p className={styles.compatibilityNote}>
              Toolbar popup: Chrome 123+.
              <br />
              Native tab menus: Chrome 150+.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className={styles.section} aria-label="Tab Group Share overview">
      <div className={styles.contentWidth}>
        <img
          className={styles.productArtwork}
          src={promoBanner}
          alt="Tab Group Share: every link in a group, copied in a click. Plain Text, Markdown, and JSON."
          width="1400"
          height="560"
          loading="lazy"
        />
      </div>
    </section>

    <section className={`${styles.section} ${styles.softSection}`}>
      <div className={styles.contentWidth}>
        <div className={styles.twoColumnSection}>
          <div className={styles.sectionHeading}>
            <p className={styles.eyebrow}>Export your research</p>
            <h2>From a group of tabs to a useful list.</h2>
            <p>
              Choose a tab group and copy its titles and URLs together, in tab
              order, including duplicate entries. Your original tabs remain
              open so you can keep working.
            </p>
            <p>
              Use the toolbar popup on Chrome 123+, or use the native tab menu
              commands on Chrome 150+.
            </p>
          </div>
          <figure className={styles.demoMedia}>
            <a
              href={exportScreenshot}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View the full-size Tab Group Share export screenshot"
            >
              <img
                className={styles.productScreenshot}
                src={exportScreenshot}
                alt="Tab Group Share toolbar popup showing group selection and Plain Text, Markdown, and JSON export."
                width="1280"
                height="800"
                loading="lazy"
              />
            </a>
            <figcaption>
              Select your research group and copy it in the format you need.
              Open the image to view it at full size.
            </figcaption>
          </figure>
        </div>
        <div className={styles.exportPreviews}>
          {exportExamples.map((format) => (
            <article key={format.name} className={styles.exportPreview}>
              <h3>{format.name}</h3>
              <p>{format.description}</p>
              <div className={styles.exportCode}>
                <div className={styles.exportCodeHeader}>
                  <span aria-hidden="true">&lt;/&gt;</span>
                  <span>{format.name}</span>
                  <span className={styles.exportExampleLabel}>Example export</span>
                </div>
                <pre tabIndex={0} aria-label={format.name + " export example"}>
                  <code>{format.preview}</code>
                </pre>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className={styles.section}>
      <div className={styles.contentWidth}>
        <div className={styles.twoColumnSection}>
          <div className={styles.sectionHeading}>
            <p className={styles.eyebrow}>Import on your terms</p>
            <h2>Paste. Preview. Confirm.</h2>
            <p>
              Manually paste a list into the import page, review the preview,
              and confirm when you are ready. Nothing opens before you
              confirm, and the extension never automatically reads your
              clipboard.
            </p>
            <p>
              Append tabs to an existing group or create a new one. Tab order
              and duplicates are preserved, and existing tabs remain open.
              Valid HTTP/HTTPS URLs open as normal browser tabs.
            </p>
          </div>
          <figure className={styles.demoMedia}>
            <a
              href={importScreenshot}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View the full-size Tab Group Share import screenshot"
            >
              <img
                className={styles.productScreenshot}
                src={importScreenshot}
                alt="Tab Group Share import page showing a manually pasted list, destination group, preview, and confirmation."
                width="1280"
                height="800"
                loading="lazy"
              />
            </a>
            <figcaption>
              Review the destination and URLs before confirming an import.
              Open the image to view it at full size.
            </figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section className={styles.section}>
      <div className={styles.contentWidth}>
        <div className={styles.privacyPanel}>
          <div>
            <p className={styles.eyebrow}>Privacy and permissions</p>
            <h2>Your tab lists stay on your device.</h2>
            <p>
              Titles, URLs, group details, and manually pasted text are
              processed locally. Tab Group Share does not send this data to
              Inverlux or any external service. You decide where to paste
              or share an export.
            </p>
            <p>
              When you confirm an import, the opened websites receive normal
              browser requests under their own policies.
            </p>
          </div>
          <ul className={styles.privacyList}>
            <li>No accounts, backend, analytics, or tracking</li>
            <li>No clipboard reading or automatic clipboard access</li>
            <li>No browsing-history database access or browsing monitoring</li>
            <li>No host permissions or injected content scripts</li>
          </ul>
          <Link className={styles.secondaryLink} to="/tab-group-share/privacy">
            View the full Privacy Policy
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>

    <section className={styles.supportCta}>
      <div className={styles.narrowWidth}>
        <p className={styles.eyebrow}>Support</p>
        <h2>Questions about Tab Group Share?</h2>
        <p>Contact Inverlux LLC at support@inverlux.com.</p>
        <Link className={styles.primaryLink} to="/support">
          Get support
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  </>
);

export default TabGroupShare;
